import { accountIdFormat } from "@redotech/redo-account-id";
import { locationIdFormat } from "@redotech/redo-inventory-id";
import {
  productFamilyIdFormat,
  productIdFormat,
} from "@redotech/redo-product-id";
import { AdvancedFilterDataSchema } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import { storageLocationIdFormat } from "@redotech/redo-warehouse-id";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export const ProductVariantBinRowSchema = z.object({
  productId: productIdFormat.schema,
  familyId: productFamilyIdFormat.schema.nullable(),
  familyTitle: z.string().nullable(),
  variantTitle: z.string().nullable(),
  sku: z.string().nullable(),
  upc: z.string().nullable(),
  vendor: z.string().nullable(),
  status: z.string().nullable(),
  price: z.number().nullable(),
  cost: z.number().nullable(),
  currency: z.string().nullable(),
  imageUrl: z.string().nullable(),
  originAccountId: accountIdFormat.schema,
  originStoreName: z.string().nullable(),
  quantity: z.number(),
  available: z.number(),
  committed: z.number(),
  reserved: z.number(),
  tags: z.array(z.string()),
  familyTags: z.array(z.string()),
  updatedAt: z.string().nullable(),
  defaultReplenishmentQuantity: z.number().nullable(),
  bins: z.array(
    z.object({
      storageLocationId: storageLocationIdFormat.schema,
      binName: z.string(),
      locationId: locationIdFormat.schema,
    }),
  ),
});

export interface ProductVariantBinRow extends z.infer<
  typeof ProductVariantBinRowSchema
> {}

export const productVariantSortFields = [
  "product",
  "sku",
  "vendor",
  "status",
  "price",
  "cost",
  "quantity",
  "available",
  "updatedAt",
  "replenishmentQuantity",
] as const;

export type ProductVariantSortField = (typeof productVariantSortFields)[number];

export const getProductVariantCatalog = {
  name: "getProductVariantCatalog" as const,
  input: z.object({
    pageSize: z.number().min(1).max(1500).optional().default(20),
    page: z.number().min(0).optional().default(0),
    search: z.string().optional(),
    sortBy: createEnum(productVariantSortFields).optional(),
    sortDirection: createEnum(["asc", "desc"]).optional(),
    advancedFiltersData: z.array(AdvancedFilterDataSchema).optional(),
  }),
  output: z.object({
    rows: z.array(ProductVariantBinRowSchema),
    totalCount: z.number(),
    page: z.number(),
    pageSize: z.number(),
  }),
};

export const getProductVariantCatalogCount = {
  name: "getProductVariantCatalogCount" as const,
  input: z.object({
    search: z.string().optional(),
    advancedFiltersData: z.array(AdvancedFilterDataSchema).optional(),
  }),
  output: z.object({ totalCount: z.number() }),
};

export const productVariantFieldValueFields = ["sku"] as const;

export type ProductVariantFieldValueField =
  (typeof productVariantFieldValueFields)[number];

export const getProductVariantFieldValues = {
  name: "getProductVariantFieldValues" as const,
  input: z.object({
    field: createEnum(productVariantFieldValueFields),
    search: z.string().nullish(),
    limit: z.number().min(1).max(500).default(100),
  }),
  output: z.object({ values: z.array(z.string()) }),
};
